// /app/frontend/src/components/frugal-v2/hooks/useFrugalData.js
// Main data hook for Frugal v2 - NEW ARCHITECTURE (200 lines)

import { useState, useEffect, useCallback } from 'react';
import * as api from '../services/frugalApi';

export const useFrugalData = (token) => {
  const [data, setData] = useState({
    incomes: [],
    savings: [],
    expenses: [],
    transactions: [],
    vital_transactions: [],
    zen: null,
    contingency: null,
    pendingEmailsCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch main data, contingency, and pending emails in parallel
      const [mainData, contingencyData, emailsData] = await Promise.all([
        api.fetchFrugalData(token),
        api.fetchContingency(token),
        api.fetchPendingEmails(token).catch(() => ({ count: 0 }))
      ]);
      
      setData({
        ...mainData,
        contingency: contingencyData,
        pendingEmailsCount: emailsData?.count || 0
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Polling for pending emails every 60 seconds
  useEffect(() => {
    if (!token) return;
    
    const checkPendingEmails = async () => {
      try {
        const emailsData = await api.fetchPendingEmails(token).catch(() => ({ count: 0 }));
        setData(prev => {
          const newCount = emailsData?.count || 0;
          // Only update if count changed
          if (prev.pendingEmailsCount !== newCount) {
            return { ...prev, pendingEmailsCount: newCount };
          }
          return prev;
        });
      } catch (err) {
        console.error('Email polling failed:', err);
      }
    };

    // Check every 60 seconds
    const interval = setInterval(checkPendingEmails, 60000);
    
    return () => clearInterval(interval);
  }, [token]);

  // Silent refresh - updates all data without loading state (keeps curtain open)
  const silentRefresh = useCallback(async () => {
    if (!token) return;
    try {
      const [mainData, contingencyData] = await Promise.all([
        api.fetchFrugalData(token),
        api.fetchContingency(token)
      ]);
      setData({
        incomes: mainData.incomes || [],
        savings: mainData.savings || [],
        expenses: mainData.expenses || [],
        transactions: mainData.transactions || [],
        vital_transactions: mainData.vital_transactions || [],
        zen: mainData.zen || {},
        contingency: contingencyData || {},
        loading: false
      });
    } catch (err) {
      console.error('Silent refresh failed:', err);
    }
  }, [token]);

  // Income actions
  const addIncome = async (incomeData) => {
    const newIncome = await api.createIncome(token, incomeData);
    setData(prev => ({ ...prev, incomes: [...prev.incomes, newIncome] }));
    silentRefresh();
    return newIncome;
  };

  const editIncome = async (id, incomeData) => {
    const updated = await api.updateIncome(token, id, incomeData);
    setData(prev => ({
      ...prev,
      incomes: prev.incomes.map(i => i.id === id ? updated : i)
    }));
    silentRefresh();
    return updated;
  };

  const toggleIncome = async (id, accountId) => {
    const body = accountId ? { account_id: accountId } : {};
    const updated = await api.toggleIncomeStatus(token, id, body);
    setData(prev => ({
      ...prev,
      incomes: prev.incomes.map(i => i.id === id ? updated : i)
    }));
    silentRefresh();
  };

  const removeIncome = async (id) => {
    await api.deleteIncome(token, id);
    setData(prev => ({ ...prev, incomes: prev.incomes.filter(i => i.id !== id) }));
    silentRefresh();
  };

  // Saving actions
  const addSaving = async (savingData) => {
    const newSaving = await api.createSaving(token, savingData);
    setData(prev => ({ ...prev, savings: [...prev.savings, newSaving] }));
    silentRefresh();
    return newSaving;
  };

  const editSaving = async (id, savingData) => {
    const updated = await api.updateSaving(token, id, savingData);
    setData(prev => ({
      ...prev,
      savings: prev.savings.map(s => s.id === id ? updated : s)
    }));
    silentRefresh();
    return updated;
  };

  const toggleSaving = async (id) => {
    const updated = await api.toggleSavingPaid(token, id);
    setData(prev => ({
      ...prev,
      savings: prev.savings.map(s => s.id === id ? updated : s)
    }));
    silentRefresh();
  };

  const removeSaving = async (id) => {
    await api.deleteSaving(token, id);
    setData(prev => ({ ...prev, savings: prev.savings.filter(s => s.id !== id) }));
    silentRefresh();
  };

  // Expense actions
  const addExpense = async (expenseData) => {
    const newExpense = await api.createExpense(token, expenseData);
    setData(prev => ({ ...prev, expenses: [...prev.expenses, newExpense] }));
    silentRefresh();
    return newExpense;
  };

  const toggleExpense = async (id, accountId = null) => {
    try {
      const updated = await api.toggleExpensePaid(token, id, accountId);
      if (updated && updated.id) {
        setData(prev => ({
          ...prev,
          expenses: prev.expenses.map(e => e.id === id ? updated : e)
        }));
        silentRefresh();
      }
    } catch (error) {
      console.error('Error toggling expense:', error);
      silentRefresh();
    }
  };

  const editExpense = async (id, expenseData) => {
    const updated = await api.updateExpense(token, id, expenseData);
    setData(prev => ({
      ...prev,
      expenses: prev.expenses.map(e => e.id === id ? updated : e)
    }));
    silentRefresh();
  };

  const removeExpense = async (id) => {
    await api.deleteExpense(token, id);
    setData(prev => ({ ...prev, expenses: prev.expenses.filter(e => e.id !== id) }));
    silentRefresh();
  };

  // Transaction (unscheduled expense) actions
  const addTransaction = async (txData) => {
    // Transactions are non-recurring expenses
    const today = new Date().getDate();
    const payload = { 
      ...txData, 
      recurring: false,
      day: txData.day || today
    };
    
    console.log('[useFrugalData] addTransaction payload:', payload);
    
    const newTx = await api.createExpense(token, payload);
    setData(prev => ({ ...prev, transactions: [...prev.transactions, newTx] }));
    silentRefresh();
    return newTx;
  };

  const editTransaction = async (id, txData) => {
    const updated = await api.updateExpense(token, id, txData);
    setData(prev => ({
      ...prev,
      transactions: prev.transactions.map(t => t.id === id ? updated : t)
    }));
    silentRefresh();
    return updated;
  };

  const removeTransaction = async (id) => {
    try {
      await api.deleteExpense(token, id);
      setData(prev => ({ ...prev, transactions: prev.transactions.filter(t => t.id !== id) }));
      silentRefresh();
    } catch (error) {
      console.error('Error removing transaction:', error);
      throw error; // Re-throw to let the UI know
    }
  };

  return {
    ...data,
    loading,
    error,
    refresh: silentRefresh,
    // Income actions
    addIncome,
    editIncome,
    toggleIncome,
    removeIncome,
    // Saving actions
    addSaving,
    editSaving,
    toggleSaving,
    removeSaving,
    // Expense actions
    addExpense,
    toggleExpense,
    editExpense,
    removeExpense,
    // Transaction actions
    addTransaction,
    editTransaction,
    removeTransaction
  };
};

export default useFrugalData;
